import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import { KeyboardEvent, useEffect, useState } from "react";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";

import { IConnectedUser } from "../../interfaces/IConnectedUser.interface";
import { clockOf, nameOf, PROTOCOL_TEXT_LIMIT, timestampOfClock } from "../../utils/protocolForm";
import { useProtocolText } from "./useProtocolText";

interface ProtocolNewEntryRowProps {
	/**
	 * "row": the next empty line of the Protokoll form's table. "bar": the same fields on their
	 * own, under the Aufgaben tab's list (BCM360-381).
	 */
	layout?: "row" | "bar";
	currentPhaseId: number;
	assignable: IConnectedUser[];
	onAdd: (entry: { message: string; recordedAt: number; assigneeId?: string }) => void;
}

const NEAR_LIMIT = PROTOCOL_TEXT_LIMIT - 100;

/**
 * The next empty line of the form, always at the bottom of the table (BCM360-376). Enter adds a
 * line, Ctrl/Cmd+Enter adds the entry. The time follows the clock until someone changes it.
 */
export default function ProtocolNewEntryRow({
	layout = "row",
	currentPhaseId,
	assignable,
	onAdd,
}: ProtocolNewEntryRowProps) {
	const tf = useProtocolText();
	const [text, setText] = useState("");
	const [time, setTime] = useState(() => clockOf(Date.now()));
	const [timeTouched, setTimeTouched] = useState(false);
	const [assigneeId, setAssigneeId] = useState("");
	const [textInvalid, setTextInvalid] = useState(false);
	const [timeInvalid, setTimeInvalid] = useState(false);

	useEffect(() => {
		if (timeTouched) {
			return;
		}

		const timer = setInterval(() => setTime(clockOf(Date.now())), 15000);

		return () => clearInterval(timer);
	}, [timeTouched]);

	const add = () => {
		// Untouched, the field means "now", to the second; typed, it is that minute.
		const now = Date.now();
		const recordedAt = timeTouched ? timestampOfClock(time, now) : now;

		setTextInvalid(!text.trim());
		setTimeInvalid(recordedAt === null);

		if (!text.trim() || recordedAt === null) {
			return;
		}

		onAdd({ message: text, recordedAt, assigneeId: assigneeId || undefined });
		setText("");
		setAssigneeId("");
		setTimeTouched(false);
		setTime(clockOf(Date.now()));
	};

	const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
		if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
			event.preventDefault();
			add();
		}
	};

	const rowCell =
		"sticky bottom-0 z-[7] border-t-2 border-t-ink bg-white px-2.5 py-2 align-top shadow-[0_-6px_8px_-6px_rgba(0,0,0,0.12)]";

	const textCell = (
		<>
			<textarea
				aria-invalid={textInvalid}
				aria-label={tf("textLabel")}
				className={classNames(
					"min-h-[58px] w-full resize-y rounded-md border px-2.5 py-2 text-sm leading-snug text-text outline-none placeholder:text-muted focus:border-accent",
					textInvalid ? "border-danger" : "border-border"
				)}
				maxLength={PROTOCOL_TEXT_LIMIT}
				placeholder={tf("placeholder")}
				value={text}
				onChange={(event) => {
					setText(event.target.value);
					setTextInvalid(false);
				}}
				onKeyDown={onKeyDown}
			/>
			<div className="mt-1 flex justify-between gap-3 text-[11px] leading-snug text-muted">
				<span>
					{currentPhaseId >= 0 && (
						<>
							{tf("assignedToPhase", { phase: tf("phase", { id: currentPhaseId }) })}
							{" · "}
						</>
					)}
					{tf("shortcut")}
				</span>
				<span
					className={classNames("flex-none tabular-nums", {
						"font-semibold text-accent-strong":
							text.length >= NEAR_LIMIT && text.length < PROTOCOL_TEXT_LIMIT,
						"font-bold text-danger": text.length >= PROTOCOL_TEXT_LIMIT,
					})}
					data-testid="protocol-char-count"
				>
					{text.length} / {PROTOCOL_TEXT_LIMIT}
				</span>
			</div>
		</>
	);

	const timeCell = (
		<>
			<input
				aria-invalid={timeInvalid}
				aria-label={tf("timeLabel")}
				className={classNames(
					"w-full rounded-md border px-1.5 py-1.5 text-sm tabular-nums outline-none focus:border-accent",
					timeInvalid ? "border-danger" : "border-border"
				)}
				inputMode="numeric"
				maxLength={5}
				value={time}
				onChange={(event) => {
					setTime(event.target.value);
					setTimeTouched(true);
					setTimeInvalid(false);
				}}
			/>
			{timeInvalid && <div className="mt-1 text-[11px] text-danger">{tf("invalidTime")}</div>}
		</>
	);

	const assigneeCell = (
		<>
			<select
				aria-label={tf("assigneeLabel")}
				className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-[13px] text-text outline-none focus:border-accent"
				value={assigneeId}
				onChange={(event) => setAssigneeId(event.target.value)}
			>
				<option value="">{tf("nobody")}</option>
				{assignable.map((user) => (
					<option key={user.id} value={user.id}>
						{nameOf(user)}
					</option>
				))}
			</select>
			<div className="mt-1 text-[11px] leading-snug text-muted">{tf("assigneeHint")}</div>
		</>
	);

	const addCell = (
		<>
			<Button
				className="h-10 !w-full"
				color={ButtonColor.ACTION}
				image={<Icon className="mr-1.5 h-3.5 w-3.5" icon={faPlus} />}
				testId="protocol-add"
				title={tf("add")}
				type="button"
				onClick={add}
			/>
		</>
	);

	const cells = [textCell, timeCell, assigneeCell, addCell];

	if (layout === "bar") {
		// The form's four columns, without the table: text · Uhrzeit · zuständig · Eintragen.
		return (
			<div
				className="grid grid-cols-[minmax(0,1fr)_84px_200px_150px] rounded-[16px] bg-white shadow-[0px_1px_2px_0px_rgba(28,39,76,0.12)]"
				data-testid="protocol-new-entry"
			>
				{cells.map((content, index) => (
					<div key={index} className="px-2.5 py-2">
						{content}
					</div>
				))}
			</div>
		);
	}

	return (
		<tr data-testid="protocol-new-entry">
			{cells.map((content, index) => (
				<td key={index} className={rowCell}>
					{content}
				</td>
			))}
		</tr>
	);
}
